import { useState, useEffect, useMemo } from 'react';
import { useIsReadOnly } from '../../../../../store/reportStore';
import { useDiscipleTypes } from '../hooks/useDiscipleTypes';
import { useAddDisciple } from '../hooks/useAddDisciple';
import { validateText, formatBirthDate } from '../../../../../utils/common';
import { Disciple } from '../types/disciple';
import styles from '../Disciple3000.module.css';

const DEFAULT_DISCIPLE: Disciple = {
  name: '',
  birthdate: '',
  gender: 'M',
  typeId: 0,
};

/**
 * 제자 입력 폼 컴포넌트
 */
export const DiscipleInputForm = () => {
  const isReadOnly = useIsReadOnly();
  const { discipleTypes } = useDiscipleTypes();
  const { addDisciple, isLoading, error: addError, clearError } = useAddDisciple();

  const [discipleForm, setDiscipleForm] = useState<Disciple>(DEFAULT_DISCIPLE);
  const [isSendable, setIsSendable] = useState(false);

  /**
   * 폼 필드 업데이트 헬퍼 함수
   */
  const updateField = (field: keyof Disciple, value: string) => {
    setDiscipleForm((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * 제자 추가 버튼 클릭 시 제자 추가
   */
  const handleAddDisciple = () => {
    if (!discipleForm.name || !discipleForm.birthdate || !discipleForm.gender || !discipleForm.typeId) return;

    addDisciple(discipleForm, () => {
      // 성공 시 입력 필드 초기화
      setDiscipleForm(DEFAULT_DISCIPLE);
      clearError();
    });
  };

  /**
   * 유효성 검사 에러 메시지
   */
  const validationError = useMemo(() => {
    if (discipleForm.name && validateText(discipleForm.name)) {
      return '특수문자를 입력할 수 없습니다.';
    }
    return '';
  }, [discipleForm.name]);

  /**
   * 통합된 에러 메시지 (유효성 검사 에러 우선, 없으면 API 에러)
   */
  const errMessage = useMemo(() => {
    return validationError || addError || '';
  }, [validationError, addError]);

  /**
   * Enter 키 입력 시 제자 추가
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isSendable) {
      handleAddDisciple();
    }
  };

  /**
   * 생년월일 포맷팅 (YYYYMMDD -> YYYY-MM-DD)
   */
  const handleBirthDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatBirthDate(e.target.value);
    updateField('birthdate', formatted);
  };

  /**
   * 제출 가능 여부 확인
   */
  useEffect(() => {
    const isValid =
      discipleForm.name &&
      !validateText(discipleForm.name) &&
      discipleForm.birthdate &&
      discipleForm.birthdate.length === 10 && // YYYY-MM-DD 형식 (10자)
      discipleForm.gender &&
      discipleForm.typeId;
    setIsSendable(isValid && !isReadOnly);
  }, [discipleForm, isReadOnly]);

  return (
    <>
      <div className="col-xs-12 col-sm-12 col-md-12">
        <div className="styled-group">
          {/* 제자종류 콤보박스 */}
          <select className={`styled-left3 ${styles.discipleTypeSelect}`} value={discipleForm.typeId} onChange={(e) => updateField('typeId', e.target.value)} disabled={isReadOnly}>
            {discipleTypes.map(({ id, department, displayName, description }, index) => (
              <option key={`${id}-${index}`} value={id}>
                {department ? `[${department}] ${displayName}` : displayName}
                {description ? ` (${description})` : ''}
              </option>
            ))}
          </select>

          {/* 성별 라디오 버튼 */}
          <div className={styles.genderGroup}>
            <label className={`${styles.genderLabel} ${isReadOnly ? styles.genderLabelDisabled : styles.genderLabelEnabled}`}>
              <input
                type="radio"
                name="disciple_gender"
                value="M"
                checked={discipleForm.gender === 'M'}
                onChange={(e) => updateField('gender', e.target.value as 'M' | 'F')}
                disabled={isReadOnly}
                className={styles.genderRadio}
              />
              <span>남</span>
            </label>
            <label className={`${styles.genderLabel} ${isReadOnly ? styles.genderLabelDisabled : styles.genderLabelEnabled}`}>
              <input
                type="radio"
                name="disciple_gender"
                value="F"
                checked={discipleForm.gender === 'F'}
                onChange={(e) => updateField('gender', e.target.value as 'M' | 'F')}
                disabled={isReadOnly}
                className={styles.genderRadio}
              />
              <span>여</span>
            </label>
          </div>

          {/* 생년월일 */}
          <input
            className={`styled-left3 ${styles.birthDateInput}`}
            type="text"
            value={discipleForm.birthdate}
            placeholder="생년월일 (예: 20250101)"
            onChange={handleBirthDateChange}
            onKeyDown={handleKeyDown}
            disabled={isReadOnly}
            maxLength={10}
          />

          {/* 제자 이름 + 추가 버튼 */}
          <div className={styles.nameInputWrapper}>
            <input
              className={`styled-left3 ${styles.nameInput}`}
              type="text"
              value={discipleForm.name}
              placeholder="제자 이름을 등록해주세요"
              onChange={(e) => updateField('name', e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isReadOnly}
              maxLength={10}
            />
            <button className={`styled-right3 ${isSendable && !isLoading ? 'add' : 'disabled'}`} type="button" disabled={!isSendable || isLoading} onClick={handleAddDisciple}>
              {isLoading ? '추가 중...' : '추가'}
            </button>
          </div>
        </div>
      </div>

      {/* 에러 메시지 */}
      {errMessage && (
        <div className="col-xs-12 col-sm-12 col-md-12">
          <span className={`main-color-important ${styles.errorMessage}`}>* {errMessage}</span>
        </div>
      )}
    </>
  );
};
